import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Syringe, Stethoscope, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner, PrimaryButton } from "@/components/kit";
import { Field, inputClass } from "@/components/AuthShell";

export default function ReminderForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ dog_id: "", type: "vaccine", title: "", due_date: "", recurrence: "none", notes: "" });

  useEffect(() => {
    (async () => {
      try {
        const [d, existing] = await Promise.all([
          apiFetch("/dogs"),
          editing ? apiFetch("/reminders") : Promise.resolve(null),
        ]);
        setDogs(d || []);
        if (editing && existing) {
          const r = existing.find((x) => x.id === id);
          if (r) setForm({ dog_id: r.dog_id, type: r.type, title: r.title, due_date: r.due_date?.slice(0, 10), recurrence: r.recurrence || "none", notes: r.notes || "" });
        } else if (d?.length) {
          setForm((f) => ({ ...f, dog_id: d[0].id }));
        }
      } catch (err) {
        if (err.status === 402) {
          toast.error("Reminders are a Pro feature");
          navigate("/app/subscription?highlight=pawpal_pro", { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id, editing, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.dog_id) return toast.error("Add a dog first.");
    if (!form.due_date) return toast.error("Pick a due date.");
    setSaving(true);
    try {
      const body = { dog_id: form.dog_id, type: form.type, title: form.title.trim(), due_date: form.due_date, recurrence: form.recurrence, notes: form.notes };
      if (editing) await apiFetch(`/reminders/${id}`, { method: "PUT", body: JSON.stringify(body) });
      else await apiFetch("/reminders", { method: "POST", body: JSON.stringify(body) });
      toast.success(editing ? "Reminder updated" : "Reminder added");
      navigate("/app/reminders", { replace: true });
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FullPageSpinner />;

  const types = [
    { key: "vaccine", label: "Vaccine", icon: Syringe },
    { key: "visit", label: "Vet visit", icon: Stethoscope },
    { key: "care", label: "Care tip", icon: Lightbulb },
  ];

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader title={editing ? "Edit reminder" : "New reminder"} backTo="/app/reminders" />

      {dogs.length === 0 ? (
        <p className="text-[#4A4746]">Add a dog profile first to create reminders.</p>
      ) : (
        <form onSubmit={save}>
          <Field label="Type">
            <div className="grid grid-cols-3 gap-3">
              {types.map((t) => (
                <button
                  type="button"
                  key={t.key}
                  data-testid={`reminder-type-${t.key}`}
                  onClick={() => setForm((f) => ({ ...f, type: t.key }))}
                  className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl border-2 font-bold text-sm transition-all ${
                    form.type === t.key ? "border-[#E86A45] bg-[#FFF7F3] text-[#B54828]" : "border-[#E8E3DA] text-[#4A4746]"
                  }`}
                >
                  <t.icon size={16} /> {t.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="For which dog">
            <select data-testid="reminder-dog-select" value={form.dog_id} onChange={set("dog_id")} className={inputClass}>
              {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>

          <Field label="Title">
            <input data-testid="reminder-title-input" required value={form.title} onChange={set("title")} placeholder={form.type === "vaccine" ? "e.g. Rabies booster" : "e.g. Annual wellness exam"} className={inputClass} />
          </Field>

          <Field label="Due date">
            <input data-testid="reminder-date-input" type="date" required value={form.due_date} onChange={set("due_date")} className={inputClass} />
          </Field>

          <Field label="Repeat">
            <select data-testid="reminder-recurrence-select" value={form.recurrence} onChange={set("recurrence")} className={inputClass}>
              <option value="none">Does not repeat</option>
              <option value="yearly">Yearly</option>
            </select>
          </Field>

          <Field label="Notes (optional)">
            <textarea data-testid="reminder-notes-input" rows={2} value={form.notes} onChange={set("notes")} className={inputClass} />
          </Field>

          <PrimaryButton data-testid="reminder-save-btn" type="submit" loading={saving} className="w-full">
            {editing ? "Save changes" : "Add reminder"}
          </PrimaryButton>
        </form>
      )}
    </div>
  );
}
